/**
 * @file NoteDetailPage.tsx
 * @brief One personal note on the web, in the phone's look.
 *
 * @details
 * The web twin of mobile's `NoteDetailScreen` (DES, 30-09-2026: "the same
 * experience"). It is one plain scroll, with no cards or grid bands.
 * - **Header:** the title (editable in place, saved on blur), then
 *   "13 Sep 15:42 · 02:14" or "· Typed", plus the processing stage.
 * - **Summary** (recordings): an Original/English toggle when there is a
 *   translation.
 * - **Points:** a numbered timeline, the number on a rail broken around it.
 * - **Meetings:** one brand row that opens "Use for a meeting", with the
 *   linked meetings listed under it.
 * - **Recording:** the transcript in a brand outline bubble, clipped to five
 *   lines with Show more; the round play button with a progress ring under
 *   it; "Re-read the points" when a ready recording has none.
 * - **Tags:** round pills, plus "Add tag".
 * - **Dock:** a glass action dock at the bottom: Pin, Share, Edit, Use for a
 *   meeting (brand), Delete. The phone puts Pin and Delete on the library's
 *   long-press; a web detail page has no long-press, so they live here.
 *
 * Reading text (summary, points, transcript) is Space Grotesk, like mobile.
 *
 * Super admins (`/admin`) do not attach notes. The server's visibility
 * predicate has no admin branch, so the picker would 404 on every meeting.
 * Existing links are still listed.
 *
 * @references
 * - FRONTEND/src/screens/Notes/NoteDetailScreen.tsx (the mobile twin)
 *
 * @author DES
 * @date 21-08-2026
 * @updated 30-09-2026 — mobile look and feel
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  ChevronRight,
  Languages,
  ListOrdered,
  Loader2,
  Mic,
  PenLine,
  Pin,
  Plus,
  RefreshCw,
  Share2,
  Sparkles,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { AgendaFlag } from "@/components/icons/AgendaFlag";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { cn } from "@/shared/theme/cn";
import { dockFadeBehind } from "@/shared/theme/dockGlass";
import { OutlineBubble } from "@/components/ui/OutlineBubble";
import { BUBBLE_TONE_CLASS, TAIL, TAIL_SPACE } from "@/components/ui/outlineBubbleGeometry";
import { isRTLText } from "@/shared/utils/formatters";
import { toast } from "@/shared/hooks/useToast";
import { ApiClientError } from "@/shared/api/client";
import { Skeleton } from "@/components/ui/Skeleton";
import { HistoryLockedState } from "@/shared/history/HistoryLockedState";
import { isHistoryLockedError } from "@/shared/history/historyLock";
import { useNotesBasePath } from "./useNotesBasePath";
import {
  useDeleteNote,
  useNote,
  useNoteLinks,
  useNoteTags,
  useRegenerateNotePoints,
  useUpdateNote,
} from "./api";
import { NoteMeetingPicker } from "./NoteMeetingPicker";
import {
  DockIconButton,
  NotePlayButton,
} from "./noteUi";
import {
  formatNoteDuration,
  formatWhen,
  noteDockGlass,
  noteShareText,
} from "./noteFormat";
import { notePointLevels, notePoints, noteSummary } from "./schemas";
import { pointLabels } from "./pointLevels";
import { TagsMenu } from "./TagsMenu";

/** Mobile's section heading: 20px icon + bold 20 label, action on the right. */
function SectionHeading({
  icon: Icon,
  title,
  action,
}: {
  icon: typeof Mic;
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 text-xl font-bold text-foreground">
        <Icon className="h-5 w-5 text-brand" strokeWidth={1.5} />
        {title}
      </h2>
      {action}
    </div>
  );
}

export function NoteDetailPage() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();
  const base = useNotesBasePath() ?? "/app";
  const location = useLocation();

  const query = useNote(noteId ?? null);
  const links = useNoteLinks(noteId ?? null);
  const tagSuggestions = useNoteTags();
  const update = useUpdateNote();
  const remove = useDeleteNote();
  const regenerate = useRegenerateNotePoints();

  const note = query.data;
  const [title, setTitle] = useState("");
  const [showSpoken, setShowSpoken] = useState(false);
  const [expanded, setExpanded] = useState(false);
  /** Transcript is longer than the five lines the bubble shows. */
  const [clipped, setClipped] = useState(false);
  const transcriptRef = useRef<HTMLParagraphElement | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Two buttons open the meeting picker (the Meetings row and the dock);
  // each gets its own menu under it (30-09-2026).
  const [pickerOpen, setPickerOpen] = useState<"row" | "dock" | null>(null);
  const canAttach = base !== "/admin";

  // Seed the editable title once; a refetch mid-rename must not snap it back.
  useEffect(() => {
    if (note && !title) setTitle(note.title ?? "");
  }, [note, title]);

  const points = useMemo(() => (note ? notePoints(note) : []), [note]);
  // Sub-points (DES 12-10-2026): 1, a, b, 2 on the rail; null = all points.
  const pointLevels = useMemo(() => (note ? notePointLevels(note, points) : null), [note, points]);
  const labels = useMemo(() => pointLabels(pointLevels, points.length), [pointLevels, points]);
  const summary = note ? noteSummary(note) : "";
  const spoken = String(note?.translate_preview ?? "").trim();
  const isTyped = note?.source === "typed";
  const tags = note?.tags ?? [];
  const linkRows = links.data ?? [];

  // Measure, don't guess: offer Show more only when the clamp hides text.
  useLayoutEffect(() => {
    const el = transcriptRef.current;
    if (el && !expanded) setClipped(el.scrollHeight > el.clientHeight + 1);
  }, [note?.transcript_text, expanded]);

  const saveField = async (body: Parameters<typeof update.mutate>[0]) => {
    try {
      await update.mutateAsync(body);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not save",
        description: e instanceof ApiClientError ? e.message : "Please try again.",
      });
    }
  };

  const handleRegenerate = (force = false) => {
    if (!noteId) return;
    regenerate.mutate(
      { noteId, force },
      {
        onError: (e) => {
          const msg = e instanceof ApiClientError ? e.message : "";
          // The 409 is the edit guard: replacing your corrections is a choice.
          if (!force && msg.includes("edited")) {
            if (window.confirm(`${msg} Regenerate anyway?`)) handleRegenerate(true);
            return;
          }
          toast({
            variant: "destructive",
            title: "Could not regenerate",
            description: msg || "Please try again.",
          });
        },
      },
    );
  };

  const handleShare = async () => {
    if (!note) return;
    const text = noteShareText(note);
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title: note.title ?? "Note", text });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied", description: "The note is on your clipboard." });
    } catch (e) {
      // Closing the share sheet is not an error.
      if (e instanceof DOMException && e.name === "AbortError") return;
      toast({ variant: "destructive", title: "Could not share", description: "Please try again." });
    }
  };

  // Full width like the library (DES 30-09-2026), so "← Notes Library"
  // sits on the page's left edge.
  const shell = "flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pb-6 pt-6 md:px-8";
  // Opened from a meeting (its attached notes / agenda): back goes to that
  // meeting, not the Notes Library (DES 08-10-2026).
  const backState = location.state as { backTo?: string; backLabel?: string } | null;
  const backTo = backState?.backTo ?? `${base}/notes`;
  const backLabel = backState?.backTo ? backState.backLabel ?? "Back" : "Notes Library";

  if (query.isLoading) {
    return (
      <div className={shell}>
        {/* Skeleton in the page's shape (web-only, DES 01-10-2026): the real
            back link, then title, subtitle and the numbered Points rows. */}
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </Link>
        <div role="status" aria-label="Loading note" className="animate-pulse">
          <Skeleton still className="mt-4 h-7 w-2/3" />
          <Skeleton still className="mt-3 h-4 w-40" />
          <div className="mt-10 flex items-center gap-2">
            <Skeleton still className="h-6 w-6" />
            <Skeleton still className="h-5 w-20" />
          </div>
          <ol className="mt-4">
            {[0.9, 0.7, 0.8, 0.55].map((w, i) => (
              <li key={i} className="flex gap-3 pb-[18px] pt-1">
                <div className="flex w-8 shrink-0 justify-center">
                  <Skeleton still className="h-5 w-3.5" />
                </div>
                <Skeleton still className="h-[18px]" style={{ width: `${w * 100}%` }} />
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  }

  // B39: older than this user's history window (403 HISTORY_LOCKED).
  if (isHistoryLockedError(query.error)) {
    return (
      <div className={shell}>
        <HistoryLockedState error={query.error} onBack={() => navigate(backTo)} backLabel="Back to notes" />
      </div>
    );
  }

  if (query.error || !note) {
    return (
      <div className={shell}>
        <h1 className="text-[28px] font-bold tracking-tight">Note not found</h1>
        <p className="mt-1 text-[15px] text-muted-foreground">
          It may have been deleted, or it belongs to another account.
        </p>
        <Link
          to={`${base}/notes`}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand"
        >
          <ArrowLeft className="h-4 w-4" /> Back to notes
        </Link>
      </div>
    );
  }

  const stage =
    note.status === "processing"
      ? " · Transcribing"
      : note.status === "analyzing"
        ? " · Analyzing"
        : note.status === "error"
          ? " · Failed"
          : "";
  const subtitle = `${formatWhen(note.created_at)} · ${
    isTyped ? "Typed" : formatNoteDuration(note.duration_seconds)
  }${stage}`;
  const transcript = note.transcript_text?.trim() ?? "";
  // The row is the ACTION; the links listed under it say where the note is.
  // Naming the one meeting in the row as well (mobile's single-row form)
  // said it twice on a page that has room for the list.
  const meetingsLabel = linkRows.length ? "Use for another meeting" : "Use for a meeting";

  return (
    <div className={shell}>
      <Link
        to={backTo}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </Link>

      {/* ---------------- Header ---------------- */}
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => {
          const next = title.trim();
          if (next && next !== (note.title ?? "")) void saveField({ id: note.id, title: next });
        }}
        placeholder="Untitled note"
        maxLength={120}
        dir={isRTLText(title) ? "rtl" : "ltr"}
        aria-label="Note title"
        className="mt-3 w-full border-0 bg-transparent p-0 text-[28px] font-bold leading-9 tracking-tight text-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-0"
      />
      <p className="mt-1 flex items-center gap-2 text-[15px] text-muted-foreground">
        {note.is_pinned ? <Pin className="h-4 w-4 text-brand" strokeWidth={1.5} /> : null}
        {subtitle}
      </p>

      {/* ---------------- Status ---------------- */}
      {note.status === "processing" ? (
        <div className="mt-6 flex items-center gap-3 text-[15px] text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-brand" />
          Transcribing your note…
        </div>
      ) : null}
      {note.status === "analyzing" ? (
        <div className="mt-6 flex items-center gap-4 rounded-[20px] border border-border p-5">
          <Loader2 className="h-6 w-6 shrink-0 animate-spin text-brand" />
          <div>
            <p className="text-[17px] font-semibold">Reading your note</p>
            <p className="text-[15px] text-muted-foreground">Pulling out the points you made…</p>
          </div>
        </div>
      ) : null}
      {note.status === "error" ? (
        <p className="mt-6 text-[15px] text-destructive">
          Processing failed. Record the note again on your phone.
        </p>
      ) : null}

      {/* ---------------- Summary (recordings) ---------------- */}
      {!isTyped && summary ? (
        <section className="mt-8">
          <SectionHeading
            icon={Sparkles}
            title="Summary"
            action={
              spoken ? (
                <button
                  type="button"
                  onClick={() => setShowSpoken((v) => !v)}
                  className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold text-foreground"
                >
                  <Languages className="h-4 w-4 text-brand" />
                  {showSpoken ? "English" : "Original"}
                </button>
              ) : null
            }
          />
          <p
            dir={isRTLText(showSpoken ? spoken : summary) ? "rtl" : "ltr"}
            className="font-reading text-[17px] leading-[26px] text-foreground"
          >
            {showSpoken ? spoken : summary}
          </p>
        </section>
      ) : null}

      {/* ---------------- Points ---------------- */}
      {points.length > 0 ? (
        <section className="mt-8">
          <SectionHeading
            icon={ListOrdered}
            title="Points"
            action={
              <Link
                to={`${base}/notes/${note.id}/edit`}
                className="flex items-center gap-1.5 text-sm font-semibold text-brand"
              >
                <PenLine className="h-4 w-4" /> Edit
              </Link>
            }
          />
          <ol>
            {points.map((point, i) => {
              const last = i === points.length - 1;
              // Sub-points (DES 12-10-2026): the rail runs straight past a
              // sub-point, its letter indented past the rail.
              const sub = Boolean(pointLevels?.[i]);
              return (
                <li key={i} className="flex gap-3">
                  {sub ? (
                    <div className="flex w-8 shrink-0 flex-col items-center" aria-hidden>
                      <span className="h-3 w-0.5 bg-border" />
                      <span className={cn("w-0.5 flex-1", last ? "bg-transparent" : "bg-border")} />
                    </div>
                  ) : (
                    /* The rail is broken around the number, as on the phone. */
                    <div className="flex w-8 shrink-0 flex-col items-center" aria-hidden>
                      <span className={cn("h-1 w-0.5", i === 0 ? "bg-transparent" : "bg-border")} />
                      <span className="text-lg font-semibold leading-7 tabular-nums text-muted-foreground">
                        {labels[i]}
                      </span>
                      <span
                        className={cn("w-0.5 flex-1", last ? "bg-transparent" : "bg-border")}
                      />
                    </div>
                  )}
                  {sub ? (
                    <span
                      aria-hidden
                      className="ml-2 w-5 shrink-0 pt-[3px] font-reading text-[17px] font-semibold leading-[22px] text-muted-foreground"
                    >
                      {labels[i]}.
                    </span>
                  ) : null}
                  <p
                    dir={isRTLText(point) ? "rtl" : "ltr"}
                    className="min-w-0 flex-1 pb-[18px] pt-[3px] font-reading text-[17px] leading-[22px] text-foreground"
                  >
                    {point}
                  </p>
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}

      {/* ---------------- Meetings ---------------- */}
      {note.status === "ready" || isTyped ? (
        <section className="mt-6">
          {canAttach ? (
            <NoteMeetingPicker
              anchor={
                <button
                  type="button"
                  className="flex w-full items-center gap-2 py-2 text-left text-base font-semibold text-brand"
                >
                  {/* DES 08-10-2026: app icon for an agenda, was CalendarDays */}
                  {linkRows.length > 0 && linkRows.every((l) => l.role !== "addendum") ? (
                    <AgendaFlag className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                  ) : (
                    <CalendarDays className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                  )}
                  <span className="min-w-0 flex-1 truncate">{meetingsLabel}</span>
                  <ChevronRight className="h-5 w-5 shrink-0" />
                </button>
              }
              open={pickerOpen === "row"}
              onOpenChange={(o) => setPickerOpen(o ? "row" : null)}
              noteId={note.id}
              noteTitle={note.title ?? undefined}
            />
          ) : linkRows.length === 0 ? (
            <p className="text-[15px] text-muted-foreground">
              Notes are attached to meetings you took part in.
            </p>
          ) : null}
          {linkRows.length > 0 ? (
            <ul className="mt-1 space-y-1 pl-7">
              {linkRows.map((link) => (
                <li key={`${link.meeting_id}-${link.role}`}>
                  <Link
                    to={`${base}/meetings/${link.meeting_id}`}
                    className="flex items-center justify-between gap-3 rounded-full px-3 py-1.5 text-[15px] hover:bg-secondary"
                  >
                    <span className="min-w-0 truncate">
                      {link.title?.trim() || "Untitled meeting"}
                    </span>
                    <span className="shrink-0 text-sm text-muted-foreground">
                      {link.role === "addendum" ? "Added after" : "Agenda"}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      {/* ---------------- Recording (voice notes) ---------------- */}
      {!isTyped && note.status !== "processing" ? (
        <section className="mt-10">
          {/* The phone's Recording (NoteDetailScreen, DES 27-09-2026): ONE
              full-width brand outline bubble, "Recording" in the top break,
              the length in the bottom one, the tail at the bottom-left, and
              Play tucked under the tail beside its dot. (30-09-2026) */}
          <div className={BUBBLE_TONE_CLASS.a}>
            <OutlineBubble
              side="start"
              className="w-full px-4 py-5"
              topLabel={
                <span className="flex items-center gap-1 text-lg font-bold text-foreground">
                  <Mic className="h-[18px] w-[18px] text-brand" />
                  Recording
                </span>
              }
              bottomLabel={
                note.duration_seconds ? (
                  <span className="text-[13px] font-medium tabular-nums text-muted-foreground">
                    {formatNoteDuration(note.duration_seconds)}
                  </span>
                ) : undefined
              }
            >
              {transcript ? (
                <>
                  <p
                    ref={transcriptRef}
                    dir={isRTLText(transcript) ? "rtl" : "ltr"}
                    className={cn(
                      "whitespace-pre-line font-reading text-[17px] leading-[26px] text-foreground",
                      !expanded && "line-clamp-5",
                    )}
                  >
                    {transcript}
                  </p>
                  {clipped || expanded ? (
                    <button
                      type="button"
                      onClick={() => setExpanded((v) => !v)}
                      className="mt-2 text-sm font-semibold text-brand"
                    >
                      {expanded ? "Show less" : "Show more"}
                    </button>
                  ) : null}
                </>
              ) : (
                <p className="text-[15px] text-muted-foreground">
                  {note.status === "analyzing" ? "Reading your note…" : "No speech detected."}
                </p>
              )}
            </OutlineBubble>
          </div>
          <div
            className="flex items-center gap-3"
            // Level with the tail's tip, 6px in (mobile bubblePlaySlot).
            style={{ marginTop: -(TAIL_SPACE - TAIL), marginLeft: 6 }}
          >
            {note.recording_url ? <NotePlayButton src={note.recording_url} /> : null}
            {note.status === "ready" && transcript && points.length === 0 ? (
              <button
                type="button"
                disabled={regenerate.isPending}
                onClick={() => handleRegenerate(false)}
                className="flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-semibold disabled:opacity-60"
              >
                <RefreshCw className="h-4 w-4 text-brand" />
                {regenerate.isPending ? "Working…" : "Re-read the points"}
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* ---------------- Tags ---------------- */}
      <section className="mt-8">
        <div className="flex flex-wrap items-center gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-border bg-secondary pl-4 pr-2 text-base font-semibold"
            >
              <Tag className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                disabled={update.isPending}
                onClick={() =>
                  void saveField({ id: note.id, tags: tags.filter((t) => t !== tag) })
                }
                className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-app-card"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          {/* Tags as a dropdown under this button (DES 04-10-2026), not a
              dialog: find, add with Enter, tap to remove; saves at once. */}
          <TagsMenu
            tags={tags}
            suggestions={tagSuggestions.data ?? []}
            saving={update.isPending}
            onChange={(next) => void saveField({ id: note.id, tags: next })}
            anchor={
              <button
                type="button"
                className="inline-flex h-10 items-center gap-1.5 rounded-full border border-dashed border-border px-4 text-base font-semibold text-muted-foreground hover:bg-secondary"
              >
                <Plus className="h-4 w-4" /> {tags.length ? "Add tag" : "Add tags"}
              </button>
            }
          />
        </div>
      </section>

      {/* ---------------- Action dock ---------------- */}
      {/* THE dock fade behind it (DES 08-10-2026). */}
      <div className={cn("pointer-events-none sticky bottom-6 z-20 mt-auto flex justify-center pt-10", dockFadeBehind)}>
        <div className={cn(noteDockGlass, "pointer-events-auto flex h-14 items-center gap-1 px-2")}>
          <DockIconButton
            label={note.is_pinned ? "Unpin note" : "Pin note"}
            active={Boolean(note.is_pinned)}
            onClick={() => void saveField({ id: note.id, isPinned: !note.is_pinned })}
          >
            <Pin className={cn("h-5 w-5", note.is_pinned && "fill-current text-brand")} strokeWidth={1.5} />
          </DockIconButton>
          <DockIconButton label="Share note" onClick={() => void handleShare()}>
            <Share2 className="h-5 w-5" strokeWidth={1.5} />
          </DockIconButton>
          <DockIconButton
            label={isTyped ? "Edit note" : "Edit points"}
            // Keep where it was opened from, through the edit and back.
            onClick={() =>
              navigate(`${base}/notes/${note.id}/edit`, { state: location.state })
            }
          >
            <PenLine className="h-5 w-5" strokeWidth={1.5} />
          </DockIconButton>
          {canAttach ? (
            <NoteMeetingPicker
              anchor={
                <DockIconButton label="Use for a meeting" tone="brand">
                  {/* DES 08-10-2026: app icon, was Presentation */}
                  <BoardroomTable className="h-5 w-5" strokeWidth={1.5} />
                </DockIconButton>
              }
              open={pickerOpen === "dock"}
              onOpenChange={(o) => setPickerOpen(o ? "dock" : null)}
              noteId={note.id}
              noteTitle={note.title ?? undefined}
            />
          ) : null}
          <DockIconButton label="Delete note" tone="danger" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="h-5 w-5" strokeWidth={1.5} />
          </DockIconButton>
        </div>
      </div>


      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note?</AlertDialogTitle>
            <AlertDialogDescription>
              “{note.title?.trim() || "Untitled note"}” and its audio are removed
              for good. Any meeting it is attached to keeps its own intelligence;
              only the link goes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                remove.mutate(note.id, {
                  onSuccess: () => {
                    toast({ title: "Note deleted" });
                    // Back where it was opened from (the meeting, or the
                    // library).
                    navigate(backTo);
                  },
                  onError: () =>
                    toast({
                      variant: "destructive",
                      title: "Could not delete",
                      description: "Please try again.",
                    }),
                });
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default NoteDetailPage;
